/** Shared scope-model contract surface for the 3-primary restructure.
 *
 *  THREE primaries (mutually exclusive): personal | work | private.
 *  Sub-categories (zero or one):
 *    personal -> general | family | social | medical | financial | business
 *    work     -> employer subs (opentext today; more over time)
 *    private  -> none
 *
 *  The backend half (sibling) owns the schema + /api/scope-definitions
 *  + /api/me/employer-history. This module embeds a static fallback so
 *  the UI is correct before AND after the backend lands, and exposes
 *  the canonical {primary, sub} view shape + the Primary . Sub chip
 *  palette every surface reads. */

import { getCsrfToken } from "./auth";

function mutationHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return {
    "Content-Type": "application/json",
    ...(t ? { "X-CSRF-Token": t } : {}),
  };
}

export type Primary = "personal" | "work" | "private";

/** Active view selection. `primary: null` means "All" (no filter);
 *  `sub: null` means the primary with no sub narrowing. */
export interface ScopeView {
  primary: Primary | null;
  sub: string | null;
}

export const ALL_VIEW: ScopeView = { primary: null, sub: null };

export interface ScopeDefinitions {
  personal: string[];
  work: string[];
  private: string[];
}

/** Static fallback used until /api/scope-definitions answers. Work
 *  subs are seeded with the current employer; the live endpoint /
 *  /api/me/employer-history can extend this at runtime. */
export const FALLBACK_SCOPE_DEFS: ScopeDefinitions = {
  personal: [
    "general",
    "family",
    "social",
    "medical",
    "financial",
    "business",
  ],
  work: ["opentext"],
  private: [],
};

/** Tailwind chip classes per primary . sub. Keys are literal so the
 *  Tailwind JIT keeps the classes. */
const CHIP: Record<string, string> = {
  "personal:general":
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  "personal:family":
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  "personal:social":
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
  "personal:medical":
    "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200",
  "personal:financial":
    "bg-amber-200 text-amber-900 dark:bg-amber-800/50 dark:text-amber-100",
  "personal:business":
    "bg-emerald-200 text-emerald-900 dark:bg-emerald-800/50 dark:text-emerald-100",
  personal:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  work: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  private:
    "bg-slate-800 text-slate-100 border border-slate-500 [background-image:repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(255,255,255,0.06)_4px,rgba(255,255,255,0.06)_8px)]",
};

const TITLE: Record<Primary, string> = {
  personal: "Personal",
  work: "Work",
  private: "Private",
};

export interface ChipMeta {
  /** "Personal . Family", "Work . OpenText", "Private". */
  label: string;
  /** Tailwind classes for the chip. */
  className: string;
}

function titleCase(s: string): string {
  return s
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Resolve a packet/instance scope to its display chip. Personal with
 *  the default `general` (or no) sub renders as just "Personal".
 *  Private never shows a sub. */
export function scopeChipMeta(
  primaryRaw: string | null | undefined,
  subRaw?: string | null,
): ChipMeta {
  const primary = (primaryRaw || "personal").toLowerCase() as Primary;
  const sub = (subRaw || "").toLowerCase();
  if (primary === "private")
    return { label: "Private", className: CHIP.private };
  if (primary === "work") {
    const lbl = sub ? `Work · ${titleCase(sub)}` : "Work";
    return { label: lbl, className: CHIP.work };
  }
  // personal
  if (!sub || sub === "general" || sub === "personal")
    return { label: "Personal", className: CHIP.personal };
  const key = `personal:${sub}`;
  return {
    label: `Personal · ${titleCase(sub)}`,
    className: CHIP[key] ?? CHIP.personal,
  };
}

export function primaryTitle(p: Primary): string {
  return TITLE[p];
}

/** Best-effort load of live scope definitions; falls back to the
 *  static map so the UI never blanks before the backend lands. */
export async function loadScopeDefinitions(): Promise<ScopeDefinitions> {
  try {
    const r = await fetch("/api/onboarding/scope-definitions", {
      credentials: "same-origin",
    });
    if (!r.ok) return FALLBACK_SCOPE_DEFS;
    const body = (await r.json()) as {
      definitions?: Array<{
        scope: string;
        scope_kind?: string;
        parent_scope?: string | null;
      }>;
    };
    const defs = body.definitions ?? [];
    if (!defs.length) return FALLBACK_SCOPE_DEFS;
    const out: ScopeDefinitions = { personal: [], work: [], private: [] };
    for (const d of defs) {
      const parent = (d.parent_scope || "").toLowerCase();
      const kind = (d.scope_kind || "").toLowerCase();
      if (kind === "primary" || !parent) continue;
      if (parent === "personal") out.personal.push(d.scope);
      else if (parent === "work") out.work.push(d.scope);
    }
    return {
      personal: out.personal.length
        ? out.personal
        : FALLBACK_SCOPE_DEFS.personal,
      work: out.work.length ? out.work : FALLBACK_SCOPE_DEFS.work,
      private: [],
    };
  } catch {
    return FALLBACK_SCOPE_DEFS;
  }
}

/** Confirm a classifier-promoted scope (locks confirmed=1).
 *  Best-effort: the endpoint lands with the backend half. */
export async function confirmPacketScope(
  packetId: string,
): Promise<boolean> {
  try {
    const r = await fetch(
      `/api/inference/packet/${encodeURIComponent(packetId)}/confirm`,
      {
        method: "POST",
        headers: mutationHeaders(),
        credentials: "same-origin",
        body: "{}",
      },
    );
    return r.ok;
  } catch {
    return false;
  }
}

/** Re-scope a packet (user-inline, locks the scope). Best-effort:
 *  the endpoint lands with the backend half. */
export async function rescopePacket(
  packetId: string,
  primary: Primary,
  sub: string | null,
): Promise<boolean> {
  try {
    const r = await fetch(
      `/api/packets/${encodeURIComponent(packetId)}/scope`,
      {
        method: "POST",
        headers: mutationHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({ primary, sub }),
      },
    );
    return r.ok;
  } catch {
    return false;
  }
}
